import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../shared/ss-select-tooltip.directive';

import type { DvmPopupBlock, DvmPopupButton } from '../../../chart-components/dvm-popup/dvm-popup.component';
import { DvmBuilderStepBase } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile, PopupBlockForm } from '../../dvm-layout-builder/dvm-layout-builder.types';
import type { LabelValueOption } from '../../shared/self-service-config.types';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';

/**
 * Popup build form (POPUP): a trigger (icon / button / tile) that opens a
 * configurable dialog of content blocks (heading, text, key-value rows, a
 * dataset table snapshot, an API-fed table, buttons, dividers). Display-only:
 * no compute call — table blocks snapshot rows client-side at Generate time.
 * The tile is previewed live on the canvas while the form is edited.
 */
@Component({
  selector: 'app-popup-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, NgSelectModule, SsSelectTooltipDirective],
  templateUrl: './popup-step.component.html',
  styles: [':host { display: contents; }'],
})
export class PopupStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['POPUP'] as const;
  private readonly http = inject(HttpClient);
  private readonly ui = inject(SelfServiceUiService);

  popupTriggerMode = 'button';
  popupTriggerIcon = 'info';
  popupTriggerLabel = '';
  popupTriggerHint = '';
  popupSize = 'medium';
  popupPosition = 'center';
  popupCloseLabel = 'Close';
  popupDismissOnBackdrop = true;
  popupTriggerBg = '';
  popupTriggerText = '';
  popupTriggerRadius: number | null = null;
  popupFontSize: number | null = null;
  popupFontFamily = '';
  popupHeaderBg = '';
  popupHeaderText = '';
  popupCardHeaderBg = '';
  popupCardHeaderText = '';
  popupBodyBg = '';
  popupBodyText = '';
  popupButtonBg = '';
  popupButtonText = '';
  popupButtonRadius: number | null = null;
  popupRadius: number | null = null;
  popupFormatAccordionOpen = false;
  popupBlocks: PopupBlockForm[] = [];
  private popupBlockIdCounter = 0;

  private static readonly POPUP_FONT_FAMILIES_FALLBACK: LabelValueOption[] = [
    { label: 'Default', value: '' },
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Segoe UI', value: "'Segoe UI', Tahoma, sans-serif" },
    { label: 'Roboto', value: "Roboto, 'Segoe UI', sans-serif" },
    { label: 'Georgia', value: "Georgia, 'Times New Roman', serif" },
    { label: 'Courier New', value: "'Courier New', Courier, monospace" },
    { label: 'JetBrains Mono', value: "'JetBrains Mono', monospace" },
  ];
  get popupFontFamilies(): LabelValueOption[] {
    return this.popupOptionsList('fontFamilies', PopupStepComponent.POPUP_FONT_FAMILIES_FALLBACK);
  }

  private static readonly POPUP_TRIGGER_MODES_FALLBACK: LabelValueOption[] = [
    { label: 'Icon click', value: 'icon' },
    { label: 'Button click', value: 'button' },
    { label: 'Tile click', value: 'tile' },
  ];
  private static readonly POPUP_BLOCK_TYPES_FALLBACK: LabelValueOption[] = [
    { label: 'Heading', value: 'heading' },
    { label: 'Text', value: 'text' },
    { label: 'Key-value rows', value: 'keyValue' },
    { label: 'Data table', value: 'table' },
    { label: 'Buttons', value: 'buttons' },
    { label: 'Divider', value: 'divider' },
  ];
  private static readonly POPUP_SIZES_FALLBACK: LabelValueOption[] = [
    { label: 'Small', value: 'small' },
    { label: 'Medium', value: 'medium' },
    { label: 'Large', value: 'large' },
    { label: 'Fullscreen', value: 'fullscreen' },
  ];
  private static readonly POPUP_POSITIONS_FALLBACK: LabelValueOption[] = [
    { label: 'Center', value: 'center' },
    { label: 'Right drawer', value: 'drawer' },
  ];
  private static readonly POPUP_HEADING_LEVELS_FALLBACK: LabelValueOption[] = [
    { label: 'Large (H1)', value: 'h1' },
    { label: 'Medium (H2)', value: 'h2' },
    { label: 'Small (H3)', value: 'h3' },
  ];
  private static readonly POPUP_BUTTON_ACTIONS_FALLBACK: LabelValueOption[] = [
    { label: 'Close popup', value: 'close' },
    { label: 'Open link', value: 'link' },
    { label: 'Go to dashboard', value: 'navigate' },
  ];
  private static readonly POPUP_BUTTON_STYLES_FALLBACK: LabelValueOption[] = [
    { label: 'Primary', value: 'primary' },
    { label: 'Secondary', value: 'secondary' },
    { label: 'Link', value: 'link' },
  ];
  private static readonly POPUP_ICONS_FALLBACK: LabelValueOption[] = [
    { label: 'Info', value: 'info' },
    { label: 'Help', value: 'help' },
    { label: 'History / audit', value: 'history' },
    { label: 'Description', value: 'description' },
    { label: 'Table', value: 'table_chart' },
    { label: 'Warning', value: 'warning' },
    { label: 'None', value: '' },
  ];

  private popupOptionsList(key: string, fallback: LabelValueOption[]): LabelValueOption[] {
    const fromConfig = (this.host.layout.options as Record<string, unknown> | undefined)?.[key] as LabelValueOption[] | undefined;
    return Array.isArray(fromConfig) && fromConfig.length ? fromConfig : fallback;
  }
  get popupTriggerModes(): LabelValueOption[] {
    return this.popupOptionsList('popupTriggerModes', PopupStepComponent.POPUP_TRIGGER_MODES_FALLBACK);
  }
  get popupBlockTypes(): LabelValueOption[] {
    return this.popupOptionsList('popupBlockTypes', PopupStepComponent.POPUP_BLOCK_TYPES_FALLBACK);
  }
  get popupSizes(): LabelValueOption[] {
    return this.popupOptionsList('popupSizes', PopupStepComponent.POPUP_SIZES_FALLBACK);
  }
  get popupPositions(): LabelValueOption[] {
    return this.popupOptionsList('popupPositions', PopupStepComponent.POPUP_POSITIONS_FALLBACK);
  }
  get popupHeadingLevels(): LabelValueOption[] {
    return this.popupOptionsList('popupHeadingLevels', PopupStepComponent.POPUP_HEADING_LEVELS_FALLBACK);
  }
  get popupButtonActions(): LabelValueOption[] {
    return this.popupOptionsList('popupButtonActions', PopupStepComponent.POPUP_BUTTON_ACTIONS_FALLBACK);
  }
  get popupButtonStyles(): LabelValueOption[] {
    return this.popupOptionsList('popupButtonStyles', PopupStepComponent.POPUP_BUTTON_STYLES_FALLBACK);
  }
  get popupIcons(): LabelValueOption[] {
    return this.popupOptionsList('popupIcons', PopupStepComponent.POPUP_ICONS_FALLBACK);
  }
  private static readonly POPUP_API_METHODS_FALLBACK: LabelValueOption[] = [
    { label: 'GET', value: 'GET' },
    { label: 'POST', value: 'POST' },
  ];
  get popupApiMethods(): LabelValueOption[] {
    return this.popupOptionsList('popupApiMethods', PopupStepComponent.POPUP_API_METHODS_FALLBACK);
  }
  private static readonly POPUP_VALUE_COLORS_FALLBACK: LabelValueOption[] = [
    { label: 'Default', value: '' },
    { label: 'Red', value: 'red' },
    { label: 'Green', value: 'green' },
    { label: 'Blue', value: 'blue' },
    { label: 'Orange', value: 'orange' },
  ];
  get popupValueColors(): LabelValueOption[] {
    return this.popupOptionsList('popupValueColors', PopupStepComponent.POPUP_VALUE_COLORS_FALLBACK);
  }

  private newPopupBlock(type: PopupBlockForm['type'] = 'text'): PopupBlockForm {
    this.popupBlockIdCounter += 1;
    return {
      id: this.popupBlockIdCounter,
      type,
      text: '',
      level: 'h2',
      align: 'left',
      pairs: [{ label: '', value: '' }],
      dataset: '',
      columns: [],
      maxRows: null,
      url: '',
      method: 'GET',
      body: '',
      rowsPath: '',
      groupsPath: '',
      groupRowsField: '',
      metaFields: [{ field: '', label: '' }],
      apiColumns: [{ field: '', label: '', color: '' }],
      refreshOnOpen: false,
      buttons: [{ label: '', action: 'close', url: '', targetDashboard: '', style: 'primary' }],
    };
  }
  addPopupBlock(): void {
    this.popupBlocks = [...this.popupBlocks, this.newPopupBlock()];
    this.onPopupFormChange();
  }
  removePopupBlock(index: number): void {
    this.popupBlocks = this.popupBlocks.filter((_, i) => i !== index);
    this.onPopupFormChange();
  }
  movePopupBlock(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= this.popupBlocks.length) return;
    const next = [...this.popupBlocks];
    [next[index], next[target]] = [next[target], next[index]];
    this.popupBlocks = next;
    this.onPopupFormChange();
  }
  addPopupPair(block: PopupBlockForm): void {
    block.pairs = [...block.pairs, { label: '', value: '' }];
    this.onPopupFormChange();
  }
  removePopupPair(block: PopupBlockForm, index: number): void {
    block.pairs = block.pairs.filter((_, i) => i !== index);
    this.onPopupFormChange();
  }
  addPopupButton(block: PopupBlockForm): void {
    block.buttons = [...block.buttons, { label: '', action: 'close', url: '', targetDashboard: '', style: 'primary' }];
    this.onPopupFormChange();
  }
  removePopupButton(block: PopupBlockForm, index: number): void {
    block.buttons = block.buttons.filter((_, i) => i !== index);
    this.onPopupFormChange();
  }
  onPopupBlockDatasetChange(block: PopupBlockForm): void {
    block.columns = [];
    if (block.dataset) this.host.loadColumnsForDataset(block.dataset);
    this.onPopupFormChange();
  }

  onPopupFormChange(): void {
    if (!this.active) return;
    if (this.host.editingTileId) {
      const tile = this.host.findTileAnywhere(this.host.editingTileId);
      if (tile && tile.layoutType === 'POPUP') {
        this.host.updateTileInPlace({
          ...tile,
          title: this.host.title.trim() || tile.title,
          popupControl: { widgetData: this.buildPopupWidgetData() },
          format: this.popupCardFormat(),
        });
      }
      return;
    }
    if (!this.host.title.trim() || !this.popupBlocks.some((block) => this.popupBlockHasContent(block))) {
      this.host.removePreviewTile();
      return;
    }
    const existingIndex = this.host.previewTileId ? this.host.dashboardTiles.findIndex((t) => t.id === this.host.previewTileId) : -1;
    if (existingIndex < 0) {
      const draft = this.buildPopupTile(this.host.buildPayload());
      this.host.previewTileId = draft.id;
      this.host.dashboardTiles = [...this.host.dashboardTiles, draft];
    } else {
      const existing = this.host.dashboardTiles[existingIndex];
      const updated = [...this.host.dashboardTiles];
      updated[existingIndex] = {
        ...existing,
        title: this.host.title.trim() || 'Popup',
        payload: this.host.buildPayload(),
        popupControl: { widgetData: this.buildPopupWidgetData() },
        format: this.popupCardFormat(),
      };
      this.host.dashboardTiles = updated;
    }
  }

  private popupFormatPayload(): Record<string, unknown> {
    const format = Object.fromEntries(
      Object.entries({
        triggerBg: this.popupTriggerBg,
        triggerText: this.popupTriggerText,
        triggerRadius: this.popupTriggerRadius,
        cardHeaderBackground: this.popupCardHeaderBg,
        cardHeaderColor: this.popupCardHeaderText,
        headerBg: this.popupHeaderBg,
        headerText: this.popupHeaderText,
        bodyBg: this.popupBodyBg,
        bodyText: this.popupBodyText,
        buttonBg: this.popupButtonBg,
        buttonText: this.popupButtonText,
        buttonRadius: this.popupButtonRadius,
        popupRadius: this.popupRadius,
        fontSize: this.popupFontSize,
        fontFamily: this.popupFontFamily,
      }).filter(([, value]) => value !== '' && value != null),
    );
    return Object.keys(format).length ? { format } : {};
  }

  private popupCardFormat(): Record<string, unknown> | undefined {
    if (!this.popupCardHeaderBg && !this.popupCardHeaderText) return undefined;
    return {
      ...(this.popupCardHeaderBg ? { cardHeaderBackground: this.popupCardHeaderBg } : {}),
      ...(this.popupCardHeaderText ? { cardHeaderColor: this.popupCardHeaderText } : {}),
    };
  }
  popupBlockColumnOptions(block: PopupBlockForm): string[] {
    return block.dataset ? this.host.cachedColumns(block.dataset) || [] : [];
  }
  private popupBlockHasContent(block: PopupBlockForm): boolean {
    if (block.type === 'heading' || block.type === 'text') return !!block.text.trim();
    if (block.type === 'keyValue') return block.pairs.some((pair) => pair.label.trim() || pair.value.trim());
    if (block.type === 'table') return !!block.dataset;
    if (block.type === 'apiTable') return !!block.url.trim();
    if (block.type === 'buttons') return block.buttons.some((button) => button.label.trim());
    return true; // divider
  }

  addPopupMetaField(block: PopupBlockForm): void {
    block.metaFields = [...block.metaFields, { field: '', label: '' }];
    this.onPopupFormChange();
  }
  removePopupMetaField(block: PopupBlockForm, index: number): void {
    block.metaFields = block.metaFields.filter((_, i) => i !== index);
    this.onPopupFormChange();
  }
  addPopupApiColumn(block: PopupBlockForm): void {
    block.apiColumns = [...block.apiColumns, { field: '', label: '', color: '' }];
    this.onPopupFormChange();
  }
  removePopupApiColumn(block: PopupBlockForm, index: number): void {
    block.apiColumns = block.apiColumns.filter((_, i) => i !== index);
    this.onPopupFormChange();
  }


  buildPopupTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.host.newTileBase(payload);
    const mode = this.popupTriggerMode || 'button';
    return {
      ...base,
      colSpan: mode === 'icon' ? 2 : 3,
      rowSpan: mode === 'tile' ? 4 : 2,
      popupControl: { widgetData: this.buildPopupWidgetData() },
      format: this.popupCardFormat(),
    };
  }

  buildPopupWidgetData(): Record<string, unknown> {
    return {
      title: this.host.title.trim() || 'Popup',
      triggerMode: this.popupTriggerMode || 'button',
      triggerIcon: this.popupTriggerIcon,
      triggerLabel: this.popupTriggerLabel.trim(),
      triggerHint: this.popupTriggerHint.trim(),
      popupSize: this.popupSize || 'medium',
      popupPosition: this.popupPosition || 'center',
      closeLabel: this.popupCloseLabel.trim(),
      dismissOnBackdrop: this.popupDismissOnBackdrop !== false,
      triggerBg: this.popupTriggerBg,
      triggerText: this.popupTriggerText,
      ...(this.popupTriggerRadius != null ? { triggerRadius: this.popupTriggerRadius } : {}),
      ...(this.popupFontSize && this.popupFontSize > 0 ? { fontSize: this.popupFontSize } : {}),
      fontFamily: this.popupFontFamily,
      headerBg: this.popupHeaderBg,
      headerText: this.popupHeaderText,
      bodyBg: this.popupBodyBg,
      bodyText: this.popupBodyText,
      buttonBg: this.popupButtonBg,
      buttonText: this.popupButtonText,
      ...(this.popupButtonRadius != null ? { buttonRadius: this.popupButtonRadius } : {}),
      ...(this.popupRadius != null ? { popupRadius: this.popupRadius } : {}),
      blocks: this.popupBlocks
        .filter((block) => this.popupBlockHasContent(block))
        .map((block) => this.popupBlockToWidgetBlock(block)),
    };
  }

  private popupBlockToWidgetBlock(block: PopupBlockForm): DvmPopupBlock {
    if (block.type === 'table') {
      const allRows = this.host.cachedRows(block.dataset) || [];
      const hidden = new Set(((this.host.layout.options as Record<string, unknown> | undefined)?.['hiddenColumns'] as string[] | undefined) ?? []);
      const columns = block.columns.length
        ? block.columns
        : (this.host.cachedColumns(block.dataset) || []).filter((column) => !hidden.has(column));
      const max = block.maxRows && block.maxRows > 0 ? block.maxRows : 50;
      const rows = allRows.slice(0, max).map((row) => {
        const out: Record<string, unknown> = {};
        columns.forEach((column) => { out[column] = row[column]; });
        return out;
      });
      return { type: 'table', dataset: block.dataset, columns, maxRows: max, rows };
    }
    if (block.type === 'keyValue') {
      return { type: 'keyValue', pairs: block.pairs.filter((pair) => pair.label.trim() || pair.value.trim()) };
    }
    if (block.type === 'apiTable') {
      return {
        type: 'apiTable',
        url: block.url.trim(),
        method: block.method || 'GET',
        body: block.body.trim(),
        rowsPath: block.rowsPath.trim(),
        groupsPath: block.groupsPath.trim(),
        groupRowsField: block.groupRowsField.trim(),
        metaFields: block.metaFields.filter((meta) => meta.field.trim()).map((meta) => ({ field: meta.field.trim(), label: meta.label.trim() })),
        apiColumns: block.apiColumns.filter((column) => column.field.trim()).map((column) => ({ field: column.field.trim(), label: column.label.trim(), color: column.color })),
        ...(block.maxRows != null && block.maxRows > 0 ? { maxRows: block.maxRows } : {}),
        refreshOnOpen: !!block.refreshOnOpen,
      };
    }
    if (block.type === 'buttons') {
      const buttons: DvmPopupButton[] = block.buttons
        .filter((button) => button.label.trim())
        .map((button) => ({
          label: button.label.trim(),
          action: (button.action === 'link' || button.action === 'navigate' ? button.action : 'close'),
          url: button.url.trim(),
          targetDashboard: button.targetDashboard,
          style: (button.style === 'secondary' || button.style === 'link' ? button.style : 'primary'),
        }));
      return { type: 'buttons', buttons, align: block.align };
    }
    if (block.type === 'divider') return { type: 'divider' };
    return { type: block.type, text: block.text, level: block.level, align: block.align };
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    return this.popupBlocks.some((block) => this.popupBlockHasContent(block)) ? [] : ['At least one content block with content in it'];
  }

  buildPayload(): Record<string, unknown> {
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'CHART',
      chartType: 'POPUP',
      title: this.host.title.trim(),
      trigger: {
        mode: this.popupTriggerMode || 'button',
        icon: this.popupTriggerIcon,
        label: this.popupTriggerLabel.trim(),
        hint: this.popupTriggerHint.trim(),
      },
      behaviour: {
        size: this.popupSize || 'medium',
        position: this.popupPosition || 'center',
        closeLabel: this.popupCloseLabel.trim(),
        dismissOnBackdrop: this.popupDismissOnBackdrop !== false,
      },
      ...this.popupFormatPayload(),
      blocks: this.popupBlocks.map((block) => ({
        type: block.type,
        ...(block.type === 'heading' || block.type === 'text' ? { text: block.text, align: block.align } : {}),
        ...(block.type === 'heading' ? { level: block.level } : {}),
        ...(block.type === 'keyValue'
          ? { pairs: block.pairs.filter((pair) => pair.label.trim() || pair.value.trim()) }
          : {}),
        ...(block.type === 'table'
          ? { dataset: block.dataset, columns: block.columns, ...(block.maxRows != null ? { maxRows: block.maxRows } : {}) }
          : {}),
        ...(block.type === 'apiTable'
          ? {
              url: block.url.trim(),
              method: block.method || 'GET',
              ...(block.body.trim() ? { body: block.body.trim() } : {}),
              ...(block.rowsPath.trim() ? { rowsPath: block.rowsPath.trim() } : {}),
              ...(block.groupsPath.trim() ? { groupsPath: block.groupsPath.trim() } : {}),
              ...(block.groupRowsField.trim() ? { groupRowsField: block.groupRowsField.trim() } : {}),
              metaFields: block.metaFields.filter((meta) => meta.field.trim()),
              apiColumns: block.apiColumns.filter((column) => column.field.trim()),
              ...(block.maxRows != null && block.maxRows > 0 ? { maxRows: block.maxRows } : {}),
              refreshOnOpen: !!block.refreshOnOpen,
            }
          : {}),
        ...(block.type === 'buttons'
          ? { align: block.align, buttons: block.buttons.filter((button) => button.label.trim()) }
          : {}),
      })),
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    const widgetData = (tile.popupControl?.widgetData as Record<string, unknown>) || {};
    const trigger = (payload['trigger'] as Record<string, unknown>) || {};
    const behaviour = (payload['behaviour'] as Record<string, unknown>) || {};
    const style = {
      ...((payload['style'] as Record<string, unknown>) || {}),
      ...((payload['format'] as Record<string, unknown>) || {}),
    };
    this.popupTriggerMode = String(trigger['mode'] ?? widgetData['triggerMode'] ?? 'button');
    this.popupTriggerIcon = String(trigger['icon'] ?? widgetData['triggerIcon'] ?? 'info');
    this.popupTriggerLabel = String(trigger['label'] ?? widgetData['triggerLabel'] ?? '');
    this.popupTriggerHint = String(trigger['hint'] ?? widgetData['triggerHint'] ?? '');
    this.popupSize = String(behaviour['size'] ?? widgetData['popupSize'] ?? 'medium');
    this.popupPosition = String(behaviour['position'] ?? widgetData['popupPosition'] ?? 'center');
    this.popupCloseLabel = String(behaviour['closeLabel'] ?? widgetData['closeLabel'] ?? 'Close');
    this.popupDismissOnBackdrop = (behaviour['dismissOnBackdrop'] ?? widgetData['dismissOnBackdrop']) !== false;
    this.popupTriggerBg = String(style['triggerBg'] ?? widgetData['triggerBg'] ?? '');
    this.popupTriggerText = String(style['triggerText'] ?? widgetData['triggerText'] ?? '');
    const radius = Number(style['triggerRadius'] ?? widgetData['triggerRadius']);
    this.popupTriggerRadius = Number.isFinite(radius) ? radius : null;
    const fontSize = Number(style['fontSize'] ?? widgetData['fontSize']);
    this.popupFontSize = Number.isFinite(fontSize) && fontSize > 0 ? fontSize : null;
    this.popupFontFamily = String(style['fontFamily'] ?? widgetData['fontFamily'] ?? '');
    this.popupHeaderBg = String(style['headerBg'] ?? widgetData['headerBg'] ?? '');
    this.popupHeaderText = String(style['headerText'] ?? widgetData['headerText'] ?? '');
    const tileFormat = (tile.format || {}) as Record<string, unknown>;
    this.popupCardHeaderBg = String(style['cardHeaderBackground'] ?? style['cardHeaderBg'] ?? tileFormat['cardHeaderBackground'] ?? '');
    this.popupCardHeaderText = String(style['cardHeaderColor'] ?? style['cardHeaderText'] ?? tileFormat['cardHeaderColor'] ?? '');
    this.popupBodyBg = String(style['bodyBg'] ?? widgetData['bodyBg'] ?? '');
    this.popupBodyText = String(style['bodyText'] ?? widgetData['bodyText'] ?? '');
    this.popupButtonBg = String(style['buttonBg'] ?? widgetData['buttonBg'] ?? '');
    this.popupButtonText = String(style['buttonText'] ?? widgetData['buttonText'] ?? '');
    const buttonRadius = Number(style['buttonRadius'] ?? widgetData['buttonRadius']);
    this.popupButtonRadius = Number.isFinite(buttonRadius) ? buttonRadius : null;
    const popupRadius = Number(style['popupRadius'] ?? widgetData['popupRadius']);
    this.popupRadius = Number.isFinite(popupRadius) ? popupRadius : null;
    this.popupFormatAccordionOpen = !!(this.popupTriggerBg || this.popupTriggerText || this.popupTriggerRadius != null || this.popupFontSize || this.popupFontFamily || this.popupHeaderBg || this.popupHeaderText || this.popupCardHeaderBg || this.popupCardHeaderText
      || this.popupBodyBg || this.popupBodyText || this.popupButtonBg || this.popupButtonText || this.popupButtonRadius != null || this.popupRadius != null);
    const savedBlocks = Array.isArray(payload['blocks'])
      ? (payload['blocks'] as Array<Record<string, unknown>>)
      : Array.isArray(widgetData['blocks']) ? (widgetData['blocks'] as Array<Record<string, unknown>>) : [];
    this.popupBlocks = savedBlocks.map((block) => {
      const fresh = this.newPopupBlock((String(block['type'] ?? 'text') as PopupBlockForm['type']));
      fresh.text = String(block['text'] ?? '');
      fresh.level = String(block['level'] ?? 'h2');
      fresh.align = String(block['align'] ?? 'left');
      fresh.dataset = String(block['dataset'] ?? '');
      fresh.columns = Array.isArray(block['columns']) ? (block['columns'] as unknown[]).map(String) : [];
      const max = Number(block['maxRows']);
      fresh.maxRows = Number.isFinite(max) && max > 0 ? max : null;
      fresh.url = String(block['url'] ?? '');
      fresh.method = String(block['method'] ?? 'GET');
      fresh.body = String(block['body'] ?? '');
      fresh.rowsPath = String(block['rowsPath'] ?? '');
      fresh.groupsPath = String(block['groupsPath'] ?? '');
      fresh.groupRowsField = String(block['groupRowsField'] ?? '');
      fresh.refreshOnOpen = block['refreshOnOpen'] === true;
      const metaFields = Array.isArray(block['metaFields']) ? (block['metaFields'] as Array<Record<string, unknown>>) : [];
      if (metaFields.length) fresh.metaFields = metaFields.map((meta) => ({ field: String(meta['field'] ?? ''), label: String(meta['label'] ?? '') }));
      const apiColumns = Array.isArray(block['apiColumns']) ? (block['apiColumns'] as Array<Record<string, unknown>>) : [];
      if (apiColumns.length) fresh.apiColumns = apiColumns.map((column) => ({ field: String(column['field'] ?? ''), label: String(column['label'] ?? ''), color: String(column['color'] ?? '') }));
      const pairs = Array.isArray(block['pairs']) ? (block['pairs'] as Array<Record<string, unknown>>) : [];
      if (pairs.length) fresh.pairs = pairs.map((pair) => ({ label: String(pair['label'] ?? ''), value: String(pair['value'] ?? '') }));
      const buttons = Array.isArray(block['buttons']) ? (block['buttons'] as Array<Record<string, unknown>>) : [];
      if (buttons.length) {
        fresh.buttons = buttons.map((button) => ({
          label: String(button['label'] ?? ''),
          action: String(button['action'] ?? 'close'),
          url: String(button['url'] ?? ''),
          targetDashboard: String(button['targetDashboard'] ?? ''),
          style: String(button['style'] ?? 'primary'),
        }));
      }
      return fresh;
    });
    this.popupBlocks.forEach((block) => { if (block.dataset) this.host.loadColumnsForDataset(block.dataset); });
  }

  reset(): void {
    this.popupTriggerMode = 'button';
    this.popupTriggerIcon = 'info';
    this.popupTriggerLabel = '';
    this.popupTriggerHint = '';
    this.popupSize = 'medium';
    this.popupPosition = 'center';
    this.popupCloseLabel = 'Close';
    this.popupDismissOnBackdrop = true;
    this.popupTriggerBg = '';
    this.popupTriggerText = '';
    this.popupTriggerRadius = null;
    this.popupFontSize = null;
    this.popupFontFamily = '';
    this.popupHeaderBg = '';
    this.popupHeaderText = '';
    this.popupCardHeaderBg = '';
    this.popupCardHeaderText = '';
    this.popupBodyBg = '';
    this.popupBodyText = '';
    this.popupButtonBg = '';
    this.popupButtonText = '';
    this.popupButtonRadius = null;
    this.popupRadius = null;
    this.popupFormatAccordionOpen = false;
    this.popupBlocks = [];
  }

  /** A fresh popup starts with one empty block; restored blocks load their table datasets' columns. */
  onLayoutActivated(): void {
    if (!this.popupBlocks.length) this.addPopupBlock();
    this.popupBlocks.forEach((block) => { if (block.dataset) this.host.loadColumnsForDataset(block.dataset); });
  }

  /** Promotes the live preview tile (or adds a new one); the POST only persists the configuration. */
  generate(payload: Record<string, unknown>, url: string): boolean {
    this.host.isGenerating = false;
    let updated = false;
    if (this.host.previewTileId && this.host.dashboardTiles.some((t) => t.id === this.host.previewTileId)) {
      const id = this.host.previewTileId;
      this.host.dashboardTiles = this.host.dashboardTiles.map((t) =>
        t.id === id
          ? { ...t, payload, title: this.host.title.trim() || t.title, popupControl: { widgetData: this.buildPopupWidgetData() }, format: this.popupCardFormat() }
          : t,
      );
      this.host.previewTileId = null;
    } else {
      updated = this.host.addTile(this.buildPopupTile(payload));
    }
    this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: () => this.ui.showSuccess(updated ? 'Popup updated on the dashboard.' : 'Popup added to the dashboard.'),
      error: () => this.ui.showError('Popup added, but saving its configuration failed.'),
    });
    return true;
  }
}
